import React, { ReactElement, useContext, useState } from "react";
import { Box, MenuItem, Typography } from "@material-ui/core";
import TestRequestBoard from "./TestRequestsBoard/TestRequestBoard";
import TestRequestsOverviewTable from "./TestRequestsTable/TestRequestsOverviewTable";
import ViewMode from "../../models/ViewMode";
import {
  getRowsPerPage,
  SortDirection,
  setSearchTermsBasicDashboard,
  setPrioritySearchTermDashboard,
} from "../../helper/UserSettingsHelper";
import { TestRequestAdvancedSearchRQ } from "../../api/models/advanced-search/TestRequestAdvancedSearchRQ";
import WaitingFilter from "../../api/models/WaitingFilter";
import {
  defaultSortColumn,
  defaultSortColumnLabel,
  sortColumnsDictionary,
  TestRequestOverviewColumnLabels,
  TestRequestSortColumn,
} from "./TestRequestsTable/TestRequestsOverviewColumns";
import UserReferenceDefault from "../../api/models/advanced-search/UserReferenceDefault";
import LaboratoryResponse from "../../api/models/LaboratoryResponse";
import useEnv from "../../hooks/env";
import TestRequestApi from "../../api/services/TestRequestApi";
import AdvancedSearchApi from "../../api/services/AdvancedSearchApi";
import { DashboardContext } from "../../contexts/DashboardContext";
import OrderedRequestHelper from "../../helper/OrderedRequestHelper";
import CustomDialog from "../../components/Dialog/CustomDialog";
import InfoBox from "../../components/InfoBox";
import CustomDropdown from "../../components/Input/CustomDropdown";
import CustomButton from "../../components/Button/CustomButton";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import GenericPermissionContext from "../../contexts/GenericPermissionContext";
import PrioritySearchTerm from "../../api/models/test-request/PrioritySearchTerm";

interface TestRequestsResultViewProps {
  viewMode: ViewMode;
  advancedSearch?: TestRequestAdvancedSearchRQ;
  retriggerSearch?: boolean;
  searchArchive?: boolean;
  statusFilterQuery?: string;
  syncWithCount?: <T>(fetch: () => Promise<T>) => Promise<T>;
  basicSearchTerms?: string[];
  prioritySearchTerm?: PrioritySearchTerm;
  waiting?: boolean;
  waitingForCustomer?: WaitingFilter;
  updateHasResults?: (hasResult: boolean) => void;
  updateSearchParameter?: (newSortColumn: TestRequestSortColumn, newSortDirection: SortDirection) => void;
}

const TestRequestsResultView = ({
  viewMode,
  advancedSearch,
  retriggerSearch,
  searchArchive,
  statusFilterQuery,
  syncWithCount,
  basicSearchTerms,
  prioritySearchTerm,
  waiting,
  waitingForCustomer,
  updateHasResults,
  updateSearchParameter,
}: TestRequestsResultViewProps): ReactElement => {
  const { api } = useEnv();
  const testRequestApi = new TestRequestApi(api);
  const advancedSearchApi = new AdvancedSearchApi(api);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(getRowsPerPage());
  const [sortColumn, setSortColumn] = useState<TestRequestOverviewColumnLabels>(defaultSortColumnLabel);
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [orderedRequestHelper] = useState(new OrderedRequestHelper());
  const [isWaiting] = useState<boolean>(waiting || false);

  const [refresh, setRefresh] = useState<boolean>(false);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<{ id: string; priority: string } | null>(null);

  const { labOrUser } = useContext(DashboardContext);
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const permissions = useContext(GenericPermissionContext);

  function noSync<T>(fetch: () => Promise<T>): Promise<T> {
    return fetch();
  }

  const sync = syncWithCount || noSync;

  const fetchTestRequests = () => {
    if (advancedSearch) {
      const searchParams = {
        pageIndex,
        pageSize,
        sortColumn: sortColumnsDictionary[sortColumn] || defaultSortColumn,
        sortDirection: sortDirection || "DESC",
        requesterEmail: (labOrUser as UserReferenceDefault)?.userEmail || "",
        labKey: (labOrUser as LaboratoryResponse)?.key || "",
        statesQueryString: statusFilterQuery || "",
        waitingFilter: waitingForCustomer,
      };
      updateSearchParameter?.(searchParams.sortColumn, searchParams.sortDirection);
      return orderedRequestHelper.ordered(() =>
        advancedSearchApi.searchTestRequests(searchParams, advancedSearch, searchArchive),
      );
    }

    if (basicSearchTerms) {
      setSearchTermsBasicDashboard(basicSearchTerms);
    }

    if (prioritySearchTerm) {
      setPrioritySearchTermDashboard(prioritySearchTerm);
    }

    return orderedRequestHelper.ordered(() =>
      testRequestApi.listTestRequests(
        pageIndex,
        pageSize,
        (labOrUser as UserReferenceDefault)?.userEmail || "",
        (labOrUser as LaboratoryResponse)?.key || "",
        sortColumnsDictionary[sortColumn] || defaultSortColumn,
        sortDirection.toUpperCase(),
        basicSearchTerms || [],
        prioritySearchTerm || ("" as PrioritySearchTerm),
        statusFilterQuery || "",
        waitingForCustomer,
      ),
    );
  };

  const handlePrioritySave = () => {
    setIsLoading(true);
    testRequestApi
      .updateTestRequestPriority(selectedRow?.id!, selectedRow?.priority!)
      .catch(handleHttpError)
      .finally(() => {
        setIsLoading(false);
        setRefresh(!refresh);
        setPriorityDialogOpen(false);
      });
  };

  const handlePriorityChange = (id: string, priority: string) => {
    setPriorityDialogOpen(true);
    setSelectedRow({ id, priority });
  };

  const editPriority = permissions?.newTestRequest?.description?.viewTestRequestPriority || false;

  const priorityMessageTemplate = () => {
    if (editPriority) {
      return (
        <Typography>
          {viewMode
            ? "* To change the priority of a test request, click on the priority field in the card and select a new priority."
            : "* To change the priority of a test request, click on the priority column and select a new priority."}
        </Typography>
      );
    }
  };

  return (
    <>
      {priorityDialogOpen && (
        <CustomDialog fullWidth maxWidth="sm" open={priorityDialogOpen} onClose={() => setPriorityDialogOpen(false)}>
          <h2>CHANGE TEST REQUEST PRIORITY</h2>
          <InfoBox text={`Changes the priority of the test request ${selectedRow?.id}`}>
            <CustomDropdown
              name="priority"
              onChange={(e) => {
                setSelectedRow({ id: selectedRow?.id!, priority: (e.target as HTMLInputElement).value });
              }}
              labelText="Priority"
              value={selectedRow?.priority || ""}
            >
              <MenuItem value="NONE" disabled={selectedRow?.priority !== "NONE"}>
                <em>NONE</em>
              </MenuItem>
              <MenuItem value="LOW">LOW</MenuItem>
              <MenuItem value="MEDIUM">MEDIUM</MenuItem>
              <MenuItem value="HIGH">HIGH</MenuItem>
            </CustomDropdown>
          </InfoBox>
          <Box display="flex" justifyContent="flex-end" alignItems="flex-end" marginTop="26px">
            <CustomButton text="Save" primary onClick={handlePrioritySave} />
          </Box>
        </CustomDialog>
      )}
      {priorityMessageTemplate()}
      {viewMode ? (
        <TestRequestBoard
          permission={editPriority}
          refresh={refresh}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          waiting={isWaiting}
          waitingForCustomer={waitingForCustomer}
          statusFilterQuery={statusFilterQuery}
          fetchTestRequests={fetchTestRequests}
          sync={sync}
          basicSearchTerms={basicSearchTerms}
          prioritySearchTerm={prioritySearchTerm || ("ALL" as PrioritySearchTerm)}
          advancedSearch={advancedSearch}
          retriggerSearch={retriggerSearch}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageSize={pageSize}
          changeRequestPriority={handlePriorityChange}
        />
      ) : (
        <TestRequestsOverviewTable
          permission={editPriority}
          refresh={refresh}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          waiting={waiting}
          waitingForCustomer={waitingForCustomer}
          statusFilterQuery={statusFilterQuery}
          fetchTestRequests={fetchTestRequests}
          sync={sync}
          basicSearchTerms={basicSearchTerms}
          prioritySearchTerm={prioritySearchTerm || ("ALL" as PrioritySearchTerm)}
          advancedSearch={advancedSearch}
          retriggerSearch={retriggerSearch}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          pageSize={pageSize}
          setPageSize={setPageSize}
          sortColumn={sortColumn}
          setSortColumn={setSortColumn}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          updateHasResults={updateHasResults}
          searchArchive
          changeRequestPriority={handlePriorityChange}
        />
      )}
    </>
  );
};

export default TestRequestsResultView;
